import loading from 'assets/pictures/loading.gif'
import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { orderBy, sortBy } from 'lodash'
import Card from '../../components/card/Card'
import Table from '../../components/datatable/Table'
import { getStationType } from '../../utils/StationUtils'
import { getDate, getFullDate } from '../../utils/DateUtil'
import { OldCampaignPortletPanel } from '../../campaign/components/CampaignPortletPanel'
import MonitoredMaterielPortletPanel from '../../materiel/components/dashboard/MonitoredMaterielPortletPanel'
import ProgressCard from '../../components/card/ProgressCard'
import { getUserBookmarksByStationType } from '../../utils/UserUtil'
import { componentHasHabilitations } from '../../utils/HabilitationUtil'
import {
    H_HYDRO_MODULE,
    H_MAT_MODULE,
} from '../../account/constants/AccessRulesConstants'
import HabilitationRequired from '../../utils/components/HabilitationRequired'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import UserThunk from 'administration/components/user/actions/UserThunk'
import useStateProgress from 'utils/customHook/useStateProgress'
import HydrometryThunk from 'hydrometry/actions/HydrometryThunk'
import { HydrometryActionConstant } from 'hydrometry/reducers/HydrometryReducer'
import StationAction from '../../station/actions/StationAction'
import useAccountSetting from '../../utils/customHook/useAccountSetting'
import MapSituationPie from '../../station/components/mapSituation/MapSituationPie'
import { Grid, Grid2 } from '@mui/material'
import { NewTable } from '../../components/datatable/NewTable'

const panelTable = 'table'
const panelGraph = 'graph'
const hydroStationType = 4

const HydrometryCard = () => {
    const {
        userBookmarks,
        hydrometricStations,
        hydroStats,
        monitoredMateriels,
        citiesIndex,
        userBookmarksStatus,
        hydrometricStationsStatus,
        hydroBookmarksStatsStatus,
        monitoredStations,
    } = useSelector(store => ({
        userBookmarks: store.UserReducer.userBookmarks,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        hydroStats: store.HydrometryReducer.hydroBookmarksStats,
        monitoredMateriels: store.MaterielReducer.monitoredMateriels,
        citiesIndex: store.CityReducer.citiesIndex,
        userBookmarksStatus: store.DataManagerReducer.user.userBookmarksStatus,
        hydrometricStationsStatus: store.DataManagerReducer.hydrometry.hydrometricStationsStatus,
        hydroBookmarksStatsStatus: store.DataManagerReducer.hydrometry.hydroBookmarksStatsStatus,
        monitoredStations: store.EventsReducer.monitoredStations,
    }), shallowEqual)

    const { progress: progressBar, isLoaded: dataLoaded } = useStateProgress([userBookmarksStatus, hydrometricStationsStatus, hydroBookmarksStatsStatus])

    const dispatch = useDispatch()

    const mapSituationWidgetObj = useAccountSetting('MAP_SITUATION_DASHBOARD_WIDGET', setting => JSON.parse(setting ?? '{}'))
    const [mapSituationStats, setMapSituationStats] = useState([])

    const bookmarks = useMemo(() => getUserBookmarksByStationType(userBookmarks, STATION_TYPE_NAME.hydrometry), [userBookmarks])

    const stationsForTable = useMemo(() => bookmarks.reduce((acc, code) => {
        const bookmarksStations = bookmarks.map(code => hydrometricStations.find(s => s.code === code)).filter(p => !!p).map(s => ({ ...s, isBookmark: true }))
        const monitored = monitoredStations.filter(s => s.type === STATION_TYPE_CONSTANT.hydrometry && hydrometricStations.some(s2 => s.id === s2.id)).map(s => ({ ...s, isMonitored: true }))
        const reduced = [...bookmarksStations, ...monitored].reduce((acc, s) => {
            const found = acc.find(s2 => s.id === s2.id)
            if (found) {
                return [...acc.filter(s2 => s2.id !== s.id), { ...s, ...found }]
            }
            return [...acc, s]
        }, [])
        return orderBy(reduced, [s => s.isMonitored && s.isBookmark ? 2 : (s.isMonitored ? 1 : 0), 'name'], ['desc', 'asc'])
    }, []), [hydrometricStations, bookmarks, monitoredStations])

    useEffect(() => {
        dispatch(UserThunk.fetchBookmarks())
        dispatch(HydrometryThunk.fetchHydrometricStations())
    }, [])

    useEffect(() => {
        const hydroMapIds = Object.keys(mapSituationWidgetObj).filter(key => key.startsWith('hydrometry') && !!mapSituationWidgetObj[key]).map(k => parseInt(k.replace('hydrometry-', '')))
        if (hydroMapIds.length) {
            Promise.all(hydroMapIds.map(id => StationAction.promiseMapSituationStats('hydrometry', id))).then(jsonTab => setMapSituationStats(jsonTab.filter(t => t.length)))
        }
    }, [mapSituationWidgetObj])


    useEffect(() => {
        const ids = stationsForTable.map(b => b.id)
        dispatch(HydrometryThunk.fetchHydroBookmarksStats(ids))
    }, [bookmarks, dispatch, stationsForTable, hydrometricStations?.length])

    useEffect(() => () => dispatch(HydrometryActionConstant.resetHydroStats()), [])

    const [panelType, setPanelType] = useState(panelTable)

    const data = useMemo(() => ({
        stations: hydrometricStations,
        bookmarks,
        icon: 'multiline_chart',
        campaignPanel: null,
        campaigns: [],
        stationType: hydroStationType,
        habilitation: componentHasHabilitations(H_HYDRO_MODULE),
        monitoredMateriels: monitoredMateriels.filter(m => m.typeSite === hydroStationType),
    }), [bookmarks, hydrometricStations, monitoredMateriels])


    const mapSituationPies = mapSituationStats.map((tab, i) => {
        return (
            <Grid2 key={tab} size={4}><MapSituationPie key={i} stats={tab} st={STATION_TYPE_NAME.hydrometry} /></Grid2>
        )
    })
    const mapSituationCard = mapSituationPies.length ? (
        <Grid2 container justifyContent='center' alignItems='center' spacing={3} sx={{ paddingBottom: '10px' }}>
            {mapSituationPies}
        </Grid2>
    ) : undefined

    const bookmarksStationPanel = useMemo(() => {
        if (!stationsForTable.length && !mapSituationStats.length) {
            return null
        }
        const stationResult = stationsForTable.map(station => {
            const cityFound = citiesIndex[station.townCode]
            const stats = hydroStats.find(s => s.code === station.code)
            return {
                id: station.id,
                code: station.code,
                name: station.name,
                altitude: station.altitude,
                stationType: station.stationType ? getStationType(parseInt(station.stationType)).libelle : '',
                city: cityFound ? `${cityFound.name} [${cityFound.code}]` : station.townCode,
                lastValueHeight: {
                    color: 'white',
                    value: stats?.lastValueHeight ?? '',
                    tooltip: getFullDate(stats?.lastDateHeight).toString() ?? '',
                },
                lastValueFlow: {
                    color: 'white',
                    value: stats?.lastValueFlow ?? '',
                    tooltip: getFullDate(stats?.lastDateFlow).toString() ?? '',
                },
                lastObservationLabel: stats?.lastObservationLabel ?? '',
                nullValue: {
                    value: '',
                    align: 'center',
                    leftIcon: station.isMonitored && station.isBookmark ? {
                        icon: 'notificationsNone',
                        color: 'red',
                        tooltip: i18n.oversight,
                    } : undefined,
                    rightIcon: {
                        icon: station.isBookmark ? 'star' : 'notificationsNone',
                        color: station.isBookmark ? 'orange' : 'red',
                        tooltip: station.isBookmark ? i18n.bookmark : i18n.oversight,
                    },
                },
                comment: station.eventComment,
            }
        })
        if (panelType === panelTable) {
            return (
                <NewTable
                    headers={['nullValue', 'code', 'city', 'name', 'stationType', 'lastValueHeight', 'lastValueFlow', 'lastObservationLabel', 'comment']}
                    rows={stationResult}
                    onClickRow={s => dispatch(push(`/station/hydrometry/${s.id}`))}
                />
            )
        }
        return (
            <Card
                title={(
                    <a>
                        <i className='waves-effect material-icons'>grade</i>
                        {i18n.myFavourites} ({stationsForTable.length} {i18n.elements})
                    </a>
                )}
                actions={[{
                    iconName: 'list',
                    onClick: () => setPanelType(panelTable),
                }]}
            >
                {null}
            </Card>
        )
    }, [citiesIndex, stationsForTable, hydroStats, panelType])

    const bookmarksPanel = useMemo(() => {
        if (data?.bookmarks?.length > 0 && data?.stations?.length > 0) {
            return (
                <div className='row no-margin'>
                    <div className='section no-padding'>
                        <div className='row no-margin'>
                            {bookmarksStationPanel}
                        </div>
                    </div>
                </div>
            )
        }
        return null
    }, [bookmarksStationPanel, data?.bookmarks?.length, data?.stations?.length])

    const campaignsPanel = useMemo(() => {
        if (data.campaigns && data.campaigns.length > 0 && data.campaignHeader) {
            return (
                <div className='row no-margin'>
                    <div className='section no-padding'>
                        <div className='row no-margin'>
                            <OldCampaignPortletPanel
                                noHeader={true}
                                campaigns={data.campaigns}
                                stationType={STATION_TYPE_NAME.hydrometry}
                                tableHeader={data.campaignHeader}
                            />
                        </div>
                    </div>
                </div>
            )
        }
        return null
    }, [data.campaignHeader, data.campaigns])

    if (dataLoaded) {
        if (!(data.habilitation && ((data.bookmarks.length > 0 && data.stations.length > 0) || data.campaigns.length > 0 || data.monitoredMateriels.length > 0 || data?.exploitations?.length > 0 || mapSituationStats?.length > 0)) || !(bookmarksPanel || campaignsPanel || mapSituationCard)) {
            return null
        }
        return (
            <div className='row no-margin padding-top-1'>
                <Card
                    title={(
                        <a className='waves-effect' onClick={() => dispatch(push(`/${data.redirect || STATION_TYPE_NAME.hydrometry}`))}>
                            <i className='material-icons '>{data.icon}</i>
                            {i18n[STATION_TYPE_NAME.hydrometry]}
                        </a>
                    )}
                >
                    <div className='padding-1 padding-bottom-0'>
                        {mapSituationCard}
                        {bookmarksPanel}
                        {campaignsPanel}
                        {
                            data.monitoredMateriels.length ? (
                                <HabilitationRequired habilitation={H_MAT_MODULE}>
                                    <MonitoredMaterielPortletPanel noHeader={true} stationType={data.stationType} />
                                </HabilitationRequired>
                            ) : null
                        }
                    </div>
                </Card>
            </div>
        )
    }
    return (
        <div className='padding-top-1'>
            <Card>
                <div className='row no-margin center-align padding-top-5'>
                    <img src={loading} />
                    <ProgressCard withoutCard message={i18n.hydrometryCardInProgress} progress={progressBar} />
                </div>
            </Card>
        </div>
    )
}

export default HydrometryCard